import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageChrome, ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useAlerts, useDismissAlert, alertHeadline } from '@/hooks/useAlerts'
import { AlertTag } from '@/components/alerts/AlertBits'
import { ageNotice, stamp, useQueueBuild } from '@/components/alerts/ui'
import { SeedCost } from '@/components/gg/SeedChip'
import '@/styles/alerts.css'

/**
 * One alert (/app/alerts/:id). Brian's alert.html v1.4: what the push carried,
 * in full (the subject · condition that fired, the headline, the outlet and
 * when it broke, the summary), the age notice once it is over a day old (a
 * note, never a block), and the two budgets made legible. Dismiss or Build ·
 * 12. On the phone the alert then shows what happened to it; on a computer
 * the next waiting alert opens.
 */
export function AlertDetailPage() {
  const { id } = useParams()
  const nav = useNavigate()
  const desk = useIsDesktop()
  const { data: alerts, isLoading } = useAlerts()
  const dismiss = useDismissAlert()
  const qb = useQueueBuild(alertHeadline, [['alerts'], ['watch-state']])
  const a = alerts?.find((x) => x.id === id)
  const waiting = (alerts ?? []).filter((x) => x.status === 'new').sort((x, y) => Date.parse(y.broke_at) - Date.parse(x.broke_at))

  const chrome = <PageChrome title="Alert" navKey="alerts" back={{ to: ROUTES.alerts, label: 'Alerts' }} />
  if (!a) {
    return <>{chrome}<div className="gg gga gga-one"><p className="dek">{isLoading ? 'Loading' : 'No alerts yet.'}</p></div></>
  }

  // The phone stays on the alert and shows what happened to it (alert.html);
  // on a computer the next one opens (desk-split.js).
  const next = () => {
    if (!desk) return
    const i = waiting.findIndex((x) => x.id === a.id)
    const n = waiting[i + 1] ?? waiting[i - 1]
    if (n && n.id !== a.id) nav(ROUTES.alert(n.id), { replace: true })
  }
  const onDismiss = () => { dismiss.mutate(a.id); qb.toast.show(<>Dismissed. <b>It stays under Earlier.</b></>); next() }
  const onBuild = () => qb.build(a.id, () => { qb.toast.show(<>Building. <b>Emma is writing it; it lands in your Library.</b></>); next() })
  const age = ageNotice(a.broke_at)

  return (
    <>
      {chrome}
      <div className="gg gga gga-one">
        <div className="eb">Breaking alert{a.test ? ' · test' : ''}</div>
        <AlertTag a={a} />
        <h1>{a.headline}</h1>
        <div className="by">
          {a.source_url ? <a className="src" href={a.source_url} target="_blank" rel="noopener noreferrer">{a.outlet}</a> : <span className="src">{a.outlet}</span>}
          <span>{stamp(a.broke_at)}</span>
        </div>
        {a.dek && <p className="dek">{a.dek}</p>}
        {a.status === 'new' && age && <div className="age">{age}</div>}
        {a.status === 'new'
          ? <div className="banks"><span>Used 1 seed from your alert budget</span><span>Build writes it like any story</span></div>
          : <div className="done">{a.status === 'built' ? <>Built. <b>It’s in your Library.</b></> : 'Dismissed. It stays under Earlier in your alerts.'} The alert budget paid for the alert, which was delivered.</div>}
        <div className="gga-bar">
          {a.status === 'new' ? (
            <>
              <button type="button" className="gga-btn" onClick={onDismiss}>Dismiss</button>
              <button type="button" className="gga-btn gga-btn--b" onClick={onBuild}>Build<SeedCost n={qb.cost} /></button>
            </>
          ) : (
            <>
              <Link className="gga-btn" to={ROUTES.alerts}>Alerts</Link>
              {a.status === 'built' && a.story_id
                ? <Link className="gga-btn gga-btn--b" to={ROUTES.story(a.story_id)}>Open the story</Link>
                : <Link className="gga-btn gga-btn--b" to={ROUTES.feed}>Back to the brief</Link>}
            </>
          )}
        </div>
      </div>
      {qb.confirm}
      {qb.toast.node}
    </>
  )
}
